'use client';

import base, { AxiosInstance } from 'axios';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { apiConfig } from '@/config/apiConfig';
import { useRBACOptional } from '@/contexts/RBACContext';
import { fetchSubTenantMemberUserIds } from '@/lib/subTenantMemberUserIds';
import { getActiveTenantHeaders, readActiveTenantScope, resolveActiveRequestUserId } from '@/lib/activeTenantScope';

export const PredictionAxiosContext = createContext<AxiosInstance | null>(null);

/**
 * Changes when sub-tenant member IDs load, sub-account selection, or account scope changes
 * (e.g. Reset to owner). Use in hooks to refetch listings.
 */
export const PredictionSubTenantScopeContext = createContext<string>('');

function normalizeRequestPath(url: string | undefined): string {
  if (!url) return '';
  const path = url.split('?')[0];
  return path.startsWith('/') ? path : `/${path}`;
}

/** GET paths that support `member_user_ids` on predictions API (sub-tenant aggregate). */
function predictionGetNeedsMemberUserIds(path: string): boolean {
  const exact = new Set([
    '/api/v1/models',
    '/api/v1/models/dashboard',
    '/api/v1/models/search',
    '/api/v1/data/connectors',
    '/api/v1/data/imports',
  ]);
  if (exact.has(path)) return true;
  // Single-model fetch by ID (e.g. /api/v1/models/abc123): pass member_user_ids so the
  // prediction backend can authorize the org owner to retrieve any org member's model.
  if (/^\/api\/v1\/models\/[^/]+$/.test(path)) return true;
  return false;
}

function getPredictionInstance(
  auth: any,
  setSessionExpiredOpen: (open: boolean) => void,
  aggregateMemberIdsRef: MutableRefObject<number[] | null>,
  subTenantScopeActiveRef: MutableRefObject<boolean>,
  listingSubAccountIdRef: MutableRefObject<number | null>
) {
  const baseURL = apiConfig.baseUrls.prediction;
  const axios = base.create({
    baseURL: baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL,
    withCredentials: true, // send cookies so prediction API auth matches browser/FastAPI docs
  });

  axios.interceptors.request.use((req) => {
    const method = (req.method || 'get').toLowerCase();
    const tenantScope = readActiveTenantScope();
    const scopedAccountId =
      listingSubAccountIdRef.current ??
      tenantScope.activeAccountId ??
      tenantScope.rootListingAccountId ??
      null;
    const accountAggregateActive =
      subTenantScopeActiveRef.current || scopedAccountId != null;

    // Prefer the real session user for X-User-ID when localStorage is stale
    // (e.g. root member cookie with leftover owner userId in storage).
    const activeUserId = resolveActiveRequestUserId(auth?.userId);
    if (method === 'get' && accountAggregateActive) {
      const path = normalizeRequestPath(req.url);
      if (predictionGetNeedsMemberUserIds(path)) {
        const ids = aggregateMemberIdsRef.current ?? [];
        const prev =
          typeof req.params === 'object' && req.params !== null && !Array.isArray(req.params)
            ? (req.params as Record<string, unknown>)
            : {};
        req.params = {
          ...prev,
          member_user_ids: ids.map((n) => String(n)).join(','),
        };
      }
    }

    if (req.data && typeof req.data === 'object' && !(req.data instanceof FormData)) {
      if (!req.data.userID && activeUserId) req.data.userID = activeUserId;
      if (auth?.isAuthenticated && !req.data.session_token && auth?.token) {
        req.data.session_token = auth.token;
      }
    }
    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    Object.assign(req.headers, getActiveTenantHeaders());
    if (scopedAccountId != null && !req.headers['X-Active-Account-Id']) {
      req.headers['X-Active-Account-Id'] = String(scopedAccountId);
    }
    if (activeUserId) req.headers['X-User-ID'] = activeUserId;
    if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('impersonation') === 'true' &&
      activeUserId
    ) {
      req.headers['X-Impersonate-User-Id'] = String(activeUserId);
    }
    if (auth?.isAuthenticated && auth?.token && typeof window !== 'undefined' && window.localStorage.getItem('impersonation') !== 'true') {
      req.headers['Authorization'] = `Bearer ${auth.token}`;
    }
    // Forward cookies so prediction API receives same session as RBAC / FastAPI docs
    if (typeof document !== 'undefined' && document.cookie) {
      req.headers['cookies'] = document.cookie;
    }
    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (auth?.isAuthenticated) {
            location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
          } else {
            setSessionExpiredOpen(true);
          }
        }
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function PredictionAxios({ children }: { children: ReactNode }) {
  const authData = useContext(AuthContext);
  const rbac = useRBACOptional();
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);
  const subTenantScopeActiveRef = useRef(false);
  const listingSubAccountIdRef = useRef<number | null>(null);

  const tenantScope = readActiveTenantScope();
  const listingSubAccountId =
    rbac?.effectiveListingSubAccountId ??
    (rbac?.isRootContextActive && rbac?.defaultAccountId != null ? rbac.defaultAccountId : null) ??
    rbac?.selectedSubAccountId ??
    tenantScope.activeAccountId ??
    tenantScope.rootListingAccountId ??
    null;
  const isSubTenantListingScope =
    Boolean(listingSubAccountId) || Boolean(tenantScope.isAggregateSubTenantScope);
  // Clear member aggregate as soon as we leave sub-tenant scope (before useEffect runs) so GETs never reuse stale ids.
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }
  // Only activate the scope interceptor branch after member IDs have fully loaded.
  // Setting this true immediately causes member_user_ids="" to be sent in requests
  // before the async fetch resolves, making the backend return empty results.
  const subTenantMemberListReady =
    isSubTenantListingScope &&
    Boolean(subTenantMemberKey) &&
    !subTenantMemberKey.startsWith('loading:');
  subTenantScopeActiveRef.current = subTenantMemberListReady;
  listingSubAccountIdRef.current = listingSubAccountId;

  const ownerUserIdRef = useRef<number | null>(null);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id
    ? Number(rbac.userContext.owner_user_id)
    : null;
  const defaultAccountIdRef = useRef<number | null>(null);
  defaultAccountIdRef.current = rbac?.defaultAccountId ?? null;

  useEffect(() => {
    if (!listingSubAccountId) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    const orgId = rbac?.currentOrgId;
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Include org owner so root-member direct login still sees owner-created models
        // (NULL account_id rows keyed by owner_user_id).
        const ownerExtId = ownerUserIdRef.current;
        const rootAccId = defaultAccountIdRef.current;
        if (
          ownerExtId != null &&
          Number.isFinite(ownerExtId) &&
          (subId === rootAccId || tenantScope.rootListingAccountId === subId) &&
          !ids.includes(ownerExtId)
        ) {
          ids.push(ownerExtId);
        }
        if (!cancelled) {
          aggregateMemberIdsRef.current = ids;
          const sorted = [...ids].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listingSubAccountId, rbac?.currentOrgId, tenantScope.rootListingAccountId]);

  const auth = authData || {};
  const client = useMemo(
    () =>
      getPredictionInstance(
        auth,
        setSessionExpiredOpen,
        aggregateMemberIdsRef,
        subTenantScopeActiveRef,
        listingSubAccountIdRef
      ),
    [authData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
  };

  // Ignore stale subTenantMemberKey when not scoped; include account ids so "Reset to owner" bumps the key the same render.
  // Use a loading: sentinel while IDs are pending so SWR consumers don't fire prematurely.
  const effectiveMemberKey =
    !isSubTenantListingScope ? ''
    : !subTenantMemberKey || subTenantMemberKey.startsWith('loading:')
      ? `loading:${listingSubAccountId}`
      : subTenantMemberKey;
  const predictionScopeSignal = [
    effectiveMemberKey,
    String(listingSubAccountId ?? ''),
    String(rbac?.currentAccountId ?? ''),
    String(rbac?.defaultAccountId ?? ''),
    String(auth?.userId ?? ''),
    String(tenantScope.activeAccountId ?? ''),
    String(tenantScope.activeUserId ?? ''),
    String(tenantScope.rootListingAccountId ?? ''),
  ].join('|');

  return (
    <PredictionSubTenantScopeContext.Provider value={predictionScopeSignal}>
      <PredictionAxiosContext.Provider value={client}>{children}</PredictionAxiosContext.Provider>
      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">Session Expired</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">Your session has expired. Please log in again to continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </PredictionSubTenantScopeContext.Provider>
  );
}
